import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Clock, MapPin, User, Truck, CheckCircle, Wrench,
  Phone, AlertCircle, Navigation, Home, Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/* ──────────────────────────────────────────────
   STATUS CONFIG
   ────────────────────────────────────────────── */
const trackingSteps = [
  { key: 'pending', label: 'Job Created', icon: Clock, desc: 'Your service request has been received' },
  { key: 'en_route', label: 'Technician En Route', icon: Truck, desc: 'Your technician is on the way' },
  { key: 'in_progress', label: 'Work In Progress', icon: Wrench, desc: 'Your technician is working on site' },
  { key: 'completed', label: 'Job Complete', icon: CheckCircle, desc: 'Service has been completed' },
];

const getStepIndex = (status) => {
  const idx = trackingSteps.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

/* ══════════════════════════════════════════════
   CUSTOMER TRACKING PAGE
   ══════════════════════════════════════════════ */
export const CustomerTracking = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTracking = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tracking/${token}`);
      const result = await res.json();
      if (result.success) {
        setData(result);
        setError(null);
      } else {
        setError(result.error || 'Invalid tracking link');
      }
    } catch (err) {
      setError('Unable to load tracking information');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);

  // Auto-refresh every 15 seconds when en_route
  useEffect(() => {
    if (data?.status === 'en_route') {
      const interval = setInterval(fetchTracking, 15000);
      return () => clearInterval(interval);
    }
  }, [data?.status, fetchTracking]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 space-y-4">
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="tracking-error">
        <Card className="w-full max-w-md border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Tracking Not Found</h2>
            <p className="text-slate-400 text-sm mb-6">{error}</p>
            <a href="/" className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors">
              Return to RemodWorks
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStep = getStepIndex(data.status);
  const isActive = data.status === 'en_route';
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

  return (
    <div className="min-h-screen bg-slate-950" data-testid="customer-tracking-page">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-800 backdrop-blur sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-400" />
            <span className="font-semibold text-white text-sm">RemodWorks</span>
          </div>
          <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
            Live Tracking
          </Badge>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Status Hero */}
        <Card className={`border overflow-hidden ${
          isActive ? 'border-blue-500/30 bg-blue-500/5' :
          data.status === 'completed' ? 'border-emerald-500/30 bg-emerald-500/5' :
          data.status === 'in_progress' ? 'border-orange-500/30 bg-orange-500/5' :
          'border-slate-700 bg-slate-900/50'
        }`} data-testid="tracking-status-card">
          <CardContent className="p-6 text-center">
            {isActive && (
              <div className="mb-4">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-500/20 border-2 border-blue-500/40 animate-pulse mb-3">
                  <Truck className="h-8 w-8 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Your Technician is On The Way</h2>
                <p className="text-slate-400 text-sm">{data.technician_name} is heading to your location</p>
              </div>
            )}

            {data.status === 'in_progress' && (
              <div className="mb-4">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-orange-500/20 border-2 border-orange-500/40 mb-3">
                  <Wrench className="h-8 w-8 text-orange-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Work In Progress</h2>
                <p className="text-slate-400 text-sm">{data.technician_name} is working at your location</p>
              </div>
            )}

            {data.status === 'completed' && (
              <div className="mb-4">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/40 mb-3">
                  <CheckCircle className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Service Complete</h2>
                <p className="text-slate-400 text-sm">Thank you for choosing RemodWorks</p>
              </div>
            )}

            {data.status === 'pending' && (
              <div className="mb-4">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/40 mb-3">
                  <Clock className="h-8 w-8 text-yellow-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Scheduling Your Service</h2>
                <p className="text-slate-400 text-sm">We're assigning a technician to your job</p>
              </div>
            )}

            {/* ETA Display */}
            {data.eta_minutes && isActive && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="bg-slate-800/60 rounded-xl p-4">
                  <Clock className="h-5 w-5 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{data.eta_text}</div>
                  <div className="text-xs text-slate-500 mt-1">Estimated Arrival</div>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-4">
                  <Navigation className="h-5 w-5 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{data.distance_text}</div>
                  <div className="text-xs text-slate-500 mt-1">Distance Away</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <Card className="border-slate-800 bg-slate-900/50" data-testid="tracking-progress">
          <CardContent className="p-5">
            <div className="space-y-0">
              {trackingSteps.map((step, idx) => {
                const isComplete = idx < currentStep;
                const isCurrent = idx === currentStep;
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-start gap-3">
                    {/* Line + Dot */}
                    <div className="flex flex-col items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                        isComplete ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' :
                        isCurrent ? 'bg-orange-500/20 border-orange-500 text-orange-400' :
                        'bg-slate-800 border-slate-700 text-slate-600'
                      }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      {idx < trackingSteps.length - 1 && (
                        <div className={`w-0.5 h-8 ${isComplete ? 'bg-emerald-500/50' : 'bg-slate-700'}`} />
                      )}
                    </div>
                    {/* Text */}
                    <div className="pt-1 pb-4">
                      <h4 className={`text-sm font-semibold ${
                        isComplete ? 'text-emerald-400' : isCurrent ? 'text-white' : 'text-slate-600'
                      }`}>
                        {step.label}
                        {isCurrent && <span className="ml-2 text-xs font-normal text-slate-400">— Current</span>}
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Service Details */}
        <Card className="border-slate-800 bg-slate-900/50" data-testid="tracking-details">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Home className="h-4 w-4 text-orange-400" /> Service Details
            </h3>
            <div className="space-y-2 text-sm">
              {data.service_type && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Service</span>
                  <span className="text-white font-medium">{data.service_type}</span>
                </div>
              )}
              {data.job_address && (
                <div className="flex justify-between items-start">
                  <span className="text-slate-500 flex-shrink-0">Location</span>
                  <span className="text-white text-right ml-4">{data.job_address}</span>
                </div>
              )}
              {data.technician_name && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Technician</span>
                  <span className="text-white flex items-center gap-1">
                    <User className="h-3 w-3 text-orange-400" />
                    {data.technician_name}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Map — route view when en_route, street view otherwise */}
        {data.job_address && apiKey && (
          <Card className="border-slate-800 bg-slate-900/50 overflow-hidden" data-testid="tracking-map">
            <CardContent className="p-0">
              {data.status === 'en_route' && data.technician_name ? (
                <iframe
                  title="Live Route"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/directions?key=${apiKey}&destination=${encodeURIComponent(data.job_address)}&origin=${encodeURIComponent(data.job_address)}&mode=driving`}
                />
              ) : (
                <iframe
                  title="Property View"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(data.job_address)}`}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Auto-refresh indicator */}
        {isActive && (
          <p className="text-center text-xs text-slate-600">
            <RotatingDot /> Updates automatically every 15 seconds
          </p>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-slate-600">
            Need help? Call us at{' '}
            <a href="tel:+12342051010" className="text-orange-400 hover:text-orange-300 transition-colors">
              (234) 205-1010
            </a>
          </p>
          <a href="/" className="text-xs text-slate-600 hover:text-slate-400 transition-colors mt-2 inline-block">
            remodworks.com
          </a>
        </div>
      </div>
    </div>
  );
};

/* Small pulsing dot component */
const RotatingDot = () => (
  <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse mr-1.5 align-middle" />
);
